import type { PlannerProject, WardrobeModule } from "../types";
import { cornerFillerSpan, cornerNeighbourFillerSpan, cornerPlan, cornerReturnWidth, cornerRun, cornerSide, cornerToPlan } from "./corner";
import {
  installationSpanMm,
  leftFillerMm,
  rightFillerMm
} from "./installation";

const MM = 0.001;
const WALL_GAP_MM = 1;

/** Ширина комнаты (длина задней стены) задаётся в «Комнате» (приёмка 05.10, wb-04). */
export const DEFAULT_ROOM_WIDTH_MM = 7450;
export const MIN_ROOM_WIDTH_MM = 2000;
export const MAX_ROOM_WIDTH_MM = 10000;

/**
 * Геометрия комнаты в метрах. Ширина меняется вместе с проектом через
 * applyRoomSize; глубина (боковые стены) постоянная.
 */
export const ROOM_LAYOUT = {
  backWallMinX: -DEFAULT_ROOM_WIDTH_MM * MM / 2,
  backWallMaxX: DEFAULT_ROOM_WIDTH_MM * MM / 2,
  backWallZ: -2.225,
  leftWallX: -DEFAULT_ROOM_WIDTH_MM * MM / 2,
  rightWallX: DEFAULT_ROOM_WIDTH_MM * MM / 2,
  sideWallMaxZ: 3.225,
  furnitureGap: 0.04
};
export const PERPENDICULAR_WALL_CLEARANCE_MM =
  ROOM_LAYOUT.furnitureGap / MM;

export const LEFT_WALL_LENGTH_MM =
  (ROOM_LAYOUT.sideWallMaxZ - ROOM_LAYOUT.backWallZ) / MM;
export const RIGHT_WALL_LENGTH_MM =
  (ROOM_LAYOUT.sideWallMaxZ - ROOM_LAYOUT.backWallZ) / MM;
// Живые значения: меняются в applyRoomSize, импортирующие модули видят новые.
export let BACK_WALL_LENGTH_MM = DEFAULT_ROOM_WIDTH_MM;
export let WALL_ROUTE_LENGTH_MM = BACK_WALL_LENGTH_MM + RIGHT_WALL_LENGTH_MM;
export let FULL_WALL_ROUTE_LENGTH_MM = LEFT_WALL_LENGTH_MM + WALL_ROUTE_LENGTH_MM;

export const roomWidthMm = (project: Pick<PlannerProject, "room"> | undefined) => {
  const width = Number(project?.room?.width);
  return Number.isFinite(width) && width > 0
    ? Math.min(MAX_ROOM_WIDTH_MM, Math.max(MIN_ROOM_WIDTH_MM, Math.round(width)))
    : DEFAULT_ROOM_WIDTH_MM;
};

/** Подставить ширину комнаты проекта во все расчёты раскладки и сцены. */
export const applyRoomSize = (project: Pick<PlannerProject, "room"> | undefined) =>
  applyRoomWidthMm(roomWidthMm(project));

const applyRoomWidthMm = (width: number) => {
  if (width === BACK_WALL_LENGTH_MM) return;
  const half = width * MM / 2;
  ROOM_LAYOUT.backWallMinX = -half;
  ROOM_LAYOUT.backWallMaxX = half;
  ROOM_LAYOUT.leftWallX = -half;
  ROOM_LAYOUT.rightWallX = half;
  BACK_WALL_LENGTH_MM = width;
  WALL_ROUTE_LENGTH_MM = BACK_WALL_LENGTH_MM + RIGHT_WALL_LENGTH_MM;
  FULL_WALL_ROUTE_LENGTH_MM = LEFT_WALL_LENGTH_MM + WALL_ROUTE_LENGTH_MM;
};

/**
 * Расчёт для другого проекта (пробная ширина, серверная копия, витрина):
 * подставить его комнату, а после вернуть ширину текущего, иначе сцена и
 * интерфейс продолжат считать по чужой комнате.
 */
export const withRoomOf = <T,>(project: Pick<PlannerProject, "room"> | undefined, compute: () => T): T => {
  const previous = BACK_WALL_LENGTH_MM;
  applyRoomSize(project);
  try {
    return compute();
  } finally {
    applyRoomWidthMm(previous);
  }
};

export type WallSide = "left" | "back" | "right";

export interface ModulePlacement {
  module: WardrobeModule;
  wing: 0 | 1;
  wall: WallSide;
  wallOffset: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  includeItems: boolean;
}

export interface CompositionLayout {
  placements: ModulePlacement[];
  bounds: {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
    width: number;
    depth: number;
  };
}

const moduleRouteSpan = (module: WardrobeModule) =>
  module.type === "corner45"
    ? module.width + cornerReturnWidth(module)
    : installationSpanMm(module);

const cornerBackOffset = (module: WardrobeModule) => cornerSide(module) === "left"
  ? PERPENDICULAR_WALL_CLEARANCE_MM
  : BACK_WALL_LENGTH_MM - PERPENDICULAR_WALL_CLEARANCE_MM - module.width;

export const cornerWorldPlan = (module: WardrobeModule) => {
  const left = cornerSide(module) === "left";
  const originX = left ? ROOM_LAYOUT.leftWallX + ROOM_LAYOUT.furnitureGap
    : ROOM_LAYOUT.rightWallX - ROOM_LAYOUT.furnitureGap;
  const originZ = ROOM_LAYOUT.backWallZ + ROOM_LAYOUT.furnitureGap;
  return cornerPlan(module).points.map(([x, z]) => ({
    x: originX + (left ? 1 : -1) * x * MM,
    z: originZ + z * MM
  }));
};

/**
 * Доборы углового в плане (м): добор углового — в плоскости его дверей перед
 * глухой частью, добор соседа — в плоскости двери соседа у угла (31.10); нет
 * дверей — нет доборов.
 */
export const cornerWorldFillerPlans = (module: WardrobeModule) => {
  if (module.type !== "corner45" || module.door === "open") return [];
  const left = cornerSide(module) === "left";
  const originX = left ? ROOM_LAYOUT.leftWallX + ROOM_LAYOUT.furnitureGap
    : ROOM_LAYOUT.rightWallX - ROOM_LAYOUT.furnitureGap;
  const originZ = ROOM_LAYOUT.backWallZ + ROOM_LAYOUT.furnitureGap;
  return [cornerFillerSpan(module), cornerNeighbourFillerSpan(module)].map((filler) =>
    ([[filler.from, filler.near], [filler.to, filler.near], [filler.to, filler.face], [filler.from, filler.face]] as const)
      .map(([u, v]) => cornerToPlan(module, u, v))
      .map(([x, z]) => ({ x: originX + (left ? 1 : -1) * x * MM, z: originZ + z * MM })));
};

export const isStackedModule = (module: WardrobeModule) =>
  Boolean(module.stackedOnId);

const isFloorModule = (module: WardrobeModule) =>
  !isStackedModule(module);

const perpendicularWallClearanceMm = (filler: number) =>
  Math.max(0, PERPENDICULAR_WALL_CLEARANCE_MM - filler);

const validStarts = (module: WardrobeModule) => {
  if (module.type === "corner45") {
    return [cornerBackOffset(module)];
  }
  const span = moduleRouteSpan(module);
  const leftClearance = perpendicularWallClearanceMm(leftFillerMm(module));
  const rightClearance = perpendicularWallClearanceMm(rightFillerMm(module));
  return [
    [
      -LEFT_WALL_LENGTH_MM,
      Math.max(-LEFT_WALL_LENGTH_MM, -span - rightClearance)
    ],
    [
      leftClearance,
      Math.max(
        leftClearance,
        BACK_WALL_LENGTH_MM - span - rightClearance
      )
    ],
    [
      BACK_WALL_LENGTH_MM + leftClearance,
      Math.max(
        BACK_WALL_LENGTH_MM + leftClearance,
        WALL_ROUTE_LENGTH_MM - span
      )
    ]
  ] as Array<[number, number]>;
};

const normalizeWallOffset = (
  module: WardrobeModule,
  desired: number,
  direction = 0
) => {
  if (module.type === "corner45") {
    return cornerBackOffset(module);
  }

  const [left, back, right] = validStarts(module) as Array<[number, number]>;
  if (!left || !back || !right) return 0;
  if (desired <= left[1]) return Math.max(left[0], desired);
  if (desired < back[0]) {
    if (direction > 0) return back[0];
    if (direction < 0) return left[1];
    return desired - left[1] < back[0] - desired ? left[1] : back[0];
  }
  if (desired <= back[1]) return Math.max(back[0], desired);
  if (desired >= right[0]) return Math.min(right[1], desired);
  if (direction > 0) return right[0];
  if (direction < 0) return back[1];
  return desired - back[1] < right[0] - desired ? back[1] : right[0];
};

const placementAt = (
  module: WardrobeModule,
  wallOffset: number,
  wing: 0 | 1,
  includeItems: boolean,
  y = 0
): ModulePlacement => {
  const center = wallOffset + leftFillerMm(module) + module.width / 2;
  const depth = module.depth * MM;

  if (wallOffset < 0) {
    const distanceFromBack = -center;
    return {
      module,
      wing,
      wall: "left",
      wallOffset,
      x: ROOM_LAYOUT.leftWallX + ROOM_LAYOUT.furnitureGap + depth,
      y,
      z: ROOM_LAYOUT.backWallZ + distanceFromBack * MM,
      yaw: Math.PI / 2,
      includeItems
    };
  }

  if (wallOffset < BACK_WALL_LENGTH_MM) {
    return {
      module,
      wing,
      wall: "back",
      wallOffset,
      x: ROOM_LAYOUT.backWallMinX + center * MM,
      y,
      z: ROOM_LAYOUT.backWallZ + ROOM_LAYOUT.furnitureGap + depth,
      yaw: 0,
      includeItems
    };
  }

  return {
    module,
    wing,
    wall: "right",
    wallOffset,
    x: ROOM_LAYOUT.rightWallX - ROOM_LAYOUT.furnitureGap - depth,
    y,
    z:
      ROOM_LAYOUT.backWallZ +
      (center - BACK_WALL_LENGTH_MM) * MM,
    yaw: -Math.PI / 2,
    includeItems
  };
};

const transformedCorners = (placement: ModulePlacement) => {
  if (placement.module.type === "corner45" && placement.wing === 0) {
    return cornerWorldPlan(placement.module);
  }
  const width = placement.module.width * MM;
  const depth = placement.module.depth * MM;
  const leftFiller = leftFillerMm(placement.module) * MM;
  const rightFiller = rightFillerMm(placement.module) * MM;
  const cos = Math.cos(placement.yaw);
  const sin = Math.sin(placement.yaw);
  const corners: Array<[number, number]> = [
    [-width / 2 - leftFiller, 0],
    [width / 2 + rightFiller, 0],
    [-width / 2 - leftFiller, -depth],
    [width / 2 + rightFiller, -depth]
  ];

  return corners.map(([localX, localZ]) => ({
    x: placement.x + cos * localX + sin * localZ,
    z: placement.z - sin * localX + cos * localZ
  }));
};

const createBounds = (placements: ModulePlacement[]) => {
  const corners = placements.flatMap(transformedCorners);
  if (!corners.length) {
    return {
      minX: 0,
      maxX: 0,
      minZ: 0,
      maxZ: 0,
      width: 0,
      depth: 0
    };
  }
  const minX = Math.min(...corners.map((point) => point.x));
  const maxX = Math.max(...corners.map((point) => point.x));
  const minZ = Math.min(...corners.map((point) => point.z));
  const maxZ = Math.max(...corners.map((point) => point.z));
  return {
    minX,
    maxX,
    minZ,
    maxZ,
    width: maxX - minX,
    depth: maxZ - minZ
  };
};

const defaultOffsets = (project: PlannerProject) => {
  const floorModules = project.modules.filter(isFloorModule);
  const cornerIndex = floorModules.findIndex(
    (module) => module.type === "corner45"
  );
  const hasCorner = cornerIndex >= 0;
  const total = floorModules.reduce(
    (sum, module) => sum + moduleRouteSpan(module),
    0
  );
  const beforeCornerWidth = hasCorner
    ? floorModules
        .slice(0, cornerIndex)
        .reduce((sum, module) => sum + moduleRouteSpan(module), 0)
    : 0;
  const corner = hasCorner ? floorModules[cornerIndex] : undefined;
  let cursor = hasCorner && corner
    ? Math.max(
        0,
        cornerSide(corner) === "left"
          ? PERPENDICULAR_WALL_CLEARANCE_MM + corner.width + WALL_GAP_MM
          : cornerBackOffset(corner) - beforeCornerWidth
      )
    : total <= BACK_WALL_LENGTH_MM
      ? (BACK_WALL_LENGTH_MM - total) / 2
      : 0;
  const result = new Map<string, number>();

  floorModules.forEach((module) => {
    let offset: number;
    if (Number.isFinite(module.wallOffset)) {
      offset = normalizeWallOffset(module, module.wallOffset!);
    } else if (module.type === "corner45") {
      offset = cornerBackOffset(module);
    } else {
      offset = normalizeWallOffset(module, cursor, 1);
    }
    result.set(module.id, offset);
    cursor = Math.max(cursor, offset + (module.type === "corner45" && cornerSide(module) === "left"
      ? module.width : moduleRouteSpan(module)) + WALL_GAP_MM);
  });
  project.modules
    .filter(isStackedModule)
    .forEach((module) => {
      result.set(module.id, result.get(module.stackedOnId ?? "") ?? 0);
    });

  return result;
};

export const createCompositionLayout = (
  project: PlannerProject
): CompositionLayout => {
  applyRoomSize(project);
  const placements: ModulePlacement[] = [];
  const offsets = defaultOffsets(project);

  project.modules.filter(isFloorModule).forEach((module) => {
    const offset = offsets.get(module.id) ?? 0;
    placements.push(placementAt(module, offset, 0, true));
    if (module.type === "corner45") {
      const returnWidth = cornerReturnWidth(module);
      placements.push(
        placementAt({ ...module, width: returnWidth }, cornerSide(module) === "left"
          ? -returnWidth - PERPENDICULAR_WALL_CLEARANCE_MM
          : BACK_WALL_LENGTH_MM + PERPENDICULAR_WALL_CLEARANCE_MM, 1, false)
      );
    }
  });
  project.modules
    .filter(isStackedModule)
    .forEach((module) => {
      const basePlacement = placements.find(
        (placement) =>
          placement.module.id === module.stackedOnId && placement.wing === 0
      );
      if (!basePlacement) return;
      placements.push(
        placementAt(
          module,
          basePlacement.wallOffset,
          0,
          true,
          basePlacement.y + basePlacement.module.height * MM
        )
      );
    });

  return {
    placements,
    bounds: createBounds(placements)
  };
};

/**
 * Соседний шкаф углового (28.10): стоит у другой стены перед его глухой
 * частью — по боковой стене, если угловой у задней, и по задней, если угловой
 * у боковой. Ближайший к углу корпус на этой стене, не дальше 150 мм; нет —
 * null. Его глубина с дверью — глубина глухой части углового.
 */
export const cornerNeighbour = (project: PlannerProject, corner: WardrobeModule) => {
  if (corner.type !== "corner45") return null;
  const layout = createCompositionLayout(project);
  // Заглушка углового на стене соседа: по боковой — wing 1, по задней — wing 0 (там его глубина).
  const stub = layout.placements.find((placement) => placement.module.id === corner.id &&
    placement.wing === (corner.cornerRun === "side" ? 0 : 1));
  if (!stub) return null;
  const stubStart = stub.wallOffset;
  const stubEnd = stub.wallOffset + stub.module.width;
  let best: { module: WardrobeModule; gap: number } | null = null;
  for (const placement of layout.placements) {
    const module = placement.module;
    if (placement.wing !== 0 || placement.wall !== stub.wall || module.id === corner.id || isStackedModule(module) ||
      module.type === "corner45" || module.type === "end" || module.type === "mezzanine") continue;
    const start = placement.wallOffset;
    const end = start + installationSpanMm(module);
    const gap = Math.max(start - stubEnd, stubStart - end);
    if (gap <= 150 && (!best || gap < best.gap)) best = { module, gap };
  }
  return best?.module ?? null;
};

/**
 * Место соседа углового на другой стене — за добором соседа, вплотную к нему;
 * петли соседа — у угла, как у двери углового (видео Сергея 31.10: «петли в
 * углах, открываются друг на друга»). Угловой у задней стены — сосед на
 * боковой, у боковой — на задней.
 */
export const cornerNeighbourSlot = (corner: WardrobeModule, width = 600) => {
  const left = cornerSide(corner) === "left";
  const gap = PERPENDICULAR_WALL_CLEARANCE_MM;
  const back = cornerRun(corner) === "back";
  const wallOffset = back
    ? left ? -gap - cornerReturnWidth(corner) - width - 1 : BACK_WALL_LENGTH_MM + gap + cornerReturnWidth(corner) + 1
    : left ? gap + corner.width + 1 : BACK_WALL_LENGTH_MM - gap - corner.width - width - 1;
  const doorHinge: "left" | "right" = back ? left ? "right" : "left" : left ? "left" : "right";
  return { wallOffset, doorHinge, wall: (back ? left ? "left" : "right" : "back") as WallSide };
};

/**
 * Можно ли сразу поставить соседа углового: соседа ещё нет, место перед
 * глухой частью свободно и шкаф помещается на стене.
 */
export const canPlaceCornerNeighbour = (project: PlannerProject, corner: WardrobeModule, width = 600) => {
  if (corner.type !== "corner45" || cornerNeighbour(project, corner)) return false;
  const slot = cornerNeighbourSlot(corner, width);
  const probe: WardrobeModule = { id: "corner-neighbour-probe", type: "single", width, height: corner.height,
    depth: corner.depth, door: "hinged", facade: "wood", handle: "push", items: [], wallOffset: slot.wallOffset };
  const trial = { ...project, modules: [...project.modules, probe] };
  const placement = createCompositionLayout(trial).placements.find((candidate) => candidate.module.id === probe.id);
  return Boolean(placement && placement.wall === slot.wall && !moduleOverlapsFloor(trial, probe.id) &&
    cornerNeighbour(trial, corner)?.id === probe.id);
};

/**
 * Ширина композиции, как её называют (приёмка 19.10): без стыков в 1–4 мм,
 * которые раскладка оставляет между соседними корпусами у задней стены, —
 * шкаф 1200 + пенал 600 это 1800, а не «1801».
 */
export const compositionJointsMm = (layout: CompositionLayout) => {
  const row = layout.placements
    .filter((placement) => placement.wing === 0 && placement.wall === "back" && !isStackedModule(placement.module))
    .sort((first, second) => first.wallOffset - second.wallOffset);
  let joints = 0;
  for (let index = 0; index < row.length - 1; index += 1) {
    const gap = row[index + 1]!.wallOffset - (row[index]!.wallOffset + installationSpanMm(row[index]!.module));
    if (gap > 0 && gap < 5) joints += gap;
  }
  return joints;
};

export const compositionWidthMm = (layout: CompositionLayout) =>
  Math.round(layout.bounds.width / MM - compositionJointsMm(layout));

interface Footprint {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

const footprintFor = (placement: ModulePlacement): Footprint => {
  const corners = transformedCorners(placement);
  return {
    minX: Math.min(...corners.map((point) => point.x)),
    maxX: Math.max(...corners.map((point) => point.x)),
    minZ: Math.min(...corners.map((point) => point.z)),
    maxZ: Math.max(...corners.map((point) => point.z))
  };
};

const footprintsOverlap = (a: Footprint, b: Footprint) => {
  // A one-millimetre joint is valid; tolerate floating-point rotation error.
  const gap = WALL_GAP_MM * MM - 1e-8;
  return (
    a.minX < b.maxX + gap &&
    a.maxX + gap > b.minX &&
    a.minZ < b.maxZ + gap &&
    a.maxZ + gap > b.minZ
  );
};

/** Помещается ли изделие с этого места целиком на одной стене (с зазорами у стен). */
const fitsOnWall = (module: WardrobeModule, start: number) => {
  const end = start + moduleRouteSpan(module);
  const leftClearance = perpendicularWallClearanceMm(leftFillerMm(module));
  const rightClearance = perpendicularWallClearanceMm(rightFillerMm(module));
  if (start < 0) return start >= -LEFT_WALL_LENGTH_MM - 0.5 && end <= -rightClearance + 0.5;
  if (start < BACK_WALL_LENGTH_MM) return start >= leftClearance - 0.5 && end <= BACK_WALL_LENGTH_MM - rightClearance + 0.5;
  return start >= BACK_WALL_LENGTH_MM + leftClearance - 0.5 && end <= WALL_ROUTE_LENGTH_MM + 0.5;
};

export const resolveModuleWallOffset = (
  project: PlannerProject,
  moduleId: string,
  desiredOffset: number,
  direction: number
) => {
  const module = project.modules.find((candidate) => candidate.id === moduleId);
  if (!module) return desiredOffset;
  if (isStackedModule(module)) {
    const basePlacement = createCompositionLayout(project).placements.find(
      (placement) => placement.module.id === module.stackedOnId
    );
    return basePlacement?.wallOffset ?? desiredOffset;
  }
  if (module.type === "corner45") {
    return cornerBackOffset(module);
  }

  const layout = createCompositionLayout(project);
  const occupiedPlacements = layout.placements.filter(
    (placement) =>
      placement.module.id !== moduleId &&
      !isStackedModule(placement.module) &&
      !(placement.module.type === "end" &&
        placement.module.endAttachedToId === moduleId)
  );
  const occupiedFootprints = occupiedPlacements.map(footprintFor);
  const span = moduleRouteSpan(module);
  const normalized = normalizeWallOffset(module, desiredOffset, direction);
  const candidates = new Set<number>([normalized]);
  const ranges = validStarts(module) as Array<[number, number]>;

  ranges.forEach(([min, max]) => {
    candidates.add(min);
    candidates.add(max);
    for (let position = min; position <= max; position += 20) {
      candidates.add(position);
    }
  });
  project.modules
    .filter(
      (candidate) =>
        candidate.id !== moduleId && !isStackedModule(candidate)
    )
    .forEach((candidate) => {
      const intervals = layout.placements.filter(item => item.module.id === candidate.id)
        .map(placement => ({ start: placement.wallOffset,
          span: candidate.type === "corner45" ? placement.module.width : moduleRouteSpan(candidate) }));
      intervals.forEach(interval => {
        candidates.add(normalizeWallOffset(module, interval.start - span - WALL_GAP_MM, direction));
        candidates.add(normalizeWallOffset(module, interval.start + interval.span + WALL_GAP_MM, direction));
      });
  });

  // Шкаф должен целиком помещаться на своей стене: раньше широкий шкаф при
  // перетаскивании «влезал» на короткую боковую стену и торчал за комнату
  // (проверка 07.10).
  const fitsWall = (start: number) => fitsOnWall(module, start);
  const valid = [...candidates].filter((candidate) => {
    if (!fitsWall(candidate)) return false;
    const candidatePlacements =
      module.type === "corner45"
        ? [
            placementAt(module, candidate, 0, true),
            placementAt(module, BACK_WALL_LENGTH_MM, 1, false)
          ]
        : [placementAt(module, candidate, 0, true)];
    const candidateFootprints = candidatePlacements.map(footprintFor);
    return candidateFootprints.every((candidateFootprint) =>
      occupiedFootprints.every(
        (occupiedFootprint) =>
          !footprintsOverlap(candidateFootprint, occupiedFootprint)
      )
    );
  });
  // Нигде не помещается — остаётся, где стоял (если стоял), а не уезжает
  // туда, где налезет на соседей или выйдет за стену.
  if (!valid.length) return Number.isFinite(module.wallOffset) && fitsWall(module.wallOffset!) ? module.wallOffset! : normalized;

  return valid.sort((a, b) => {
    const distanceA = Math.abs(a - normalized);
    const distanceB = Math.abs(b - normalized);
    if (distanceA !== distanceB) return distanceA - distanceB;
    if (direction > 0) return b - a;
    if (direction < 0) return a - b;
    return a - b;
  })[0]!;
};

export const materializeWallOffsets = (project: PlannerProject) => {
  const layout = createCompositionLayout(project);
  return new Map(
    project.modules.map((module) => [
      module.id,
      layout.placements.find(
        (placement) =>
          placement.module.id === module.id && placement.wing === 0
      )?.wallOffset ?? 0
    ])
  );
};

export const moduleOverlapsFloor = (project: PlannerProject, moduleId: string) => {
  const placements = createCompositionLayout(project).placements.filter(p => !isStackedModule(p.module));
  return placements.filter(p => p.module.id === moduleId).some(a =>
    placements.filter(p => p.module.id !== moduleId).some(b => footprintsOverlap(footprintFor(a), footprintFor(b))));
};

/**
 * Packs floor products using the same room/collision rules as dragging.
 * Attached end modules travel with their cabinet; mezzanines follow their base.
 */
export const separateOverlappingFloorModules = (project: PlannerProject) => {
  const offsets = materializeWallOffsets(project);
  const floor = project.modules.filter(isFloorModule);
  const attached = new Set(floor.filter(module => module.type === "end" &&
    floor.some(base => base.id === module.endAttachedToId &&
      (base.type === "single" || base.type === "double"))).map(module => module.id));
  const groups = floor.filter(module => !attached.has(module.id)).map(module => {
    const ends = floor.filter(end => attached.has(end.id) && end.endAttachedToId === module.id);
    const members = [module, ...ends];
    // Ширина группы — шкаф плюс его торцы вплотную, а не по их текущим местам:
    // торец, который уже налез на шкаф, иначе не увеличивал группу, и место
    // для неё не искалось (проверка 07.10).
    const { leading, trailing } = attachedEndSpans(ends);
    const start = (offsets.get(module.id) ?? 0) - leading;
    const proxy: WardrobeModule = members.length === 1 ? { ...module, wallOffset: start } : {
      ...module, width: installationSpanMm(module) + leading + trailing, depth: Math.max(...members.map(member => member.depth)),
      fillerLeft: 0, fillerRight: 0, wallOffset: start
    };
    return { members, start, proxy };
  }).sort((a, b) => Number(b.proxy.type === "corner45") - Number(a.proxy.type === "corner45") || a.start - b.start);
  const placed: WardrobeModule[] = [];
  const nextOffsets = new Map(offsets);
  for (const group of groups) {
    const trial = { ...project, modules: [...placed, group.proxy] };
    const next = resolveModuleWallOffset(trial, group.proxy.id, group.start, 1);
    group.proxy.wallOffset = next;
    const placements = createCompositionLayout(trial).placements;
    const moving = placements.filter(placement => placement.module.id === group.proxy.id);
    const others = placements.filter(placement => placement.module.id !== group.proxy.id);
    if (moving.some(candidate => others.some(other => footprintsOverlap(footprintFor(candidate), footprintFor(other))))) {
      // No room for the whole composition: do not apply a partial rearrangement.
      return false;
    }
    group.members.forEach(member => nextOffsets.set(member.id, (offsets.get(member.id) ?? 0) + next - group.start));
    placed.push(group.proxy);
  }
  const changed = floor.some(module => Math.abs(nextOffsets.get(module.id)! - offsets.get(module.id)!) > 0.01);
  if (changed) {
    // Materialize every offset together so implicit centering cannot move neighbours again.
    floor.forEach(module => { module.wallOffset = Number(nextOffsets.get(module.id)!.toFixed(3)); });
  }
  return changed;
};

/**
 * Самая узкая комната, в которую проект встаёт без изменений: ни один шкаф
 * не переезжает на другую стену и не налезает на соседа (пересечения, которые
 * уже были, не мешают). Шкафы у правой стены едут вместе с ней.
 */
export const roomWidthMinimumMm = (project: PlannerProject) => withRoomOf(project, () => {
  const current = roomWidthMm(project);
  const floor = project.modules.filter(isFloorModule);
  const wallsOf = (candidate: PlannerProject) => new Map(createCompositionLayout(candidate).placements
    .filter(placement => placement.wing === 0).map(placement => [placement.module.id, placement.wall] as const));
  const walls = wallsOf(project);
  const overlapping = new Set(floor.filter(module => moduleOverlapsFloor(project, module.id)).map(module => module.id));
  const offsets = materializeWallOffsets(project);
  const reserve = PERPENDICULAR_WALL_CLEARANCE_MM + (floor.find(module => module.type === "corner45" && cornerSide(module) === "right")?.width ?? 0);
  const backRight = Math.max(0, ...floor
    .filter(module => module.type !== "corner45" && walls.get(module.id) === "back")
    .map(module => (offsets.get(module.id) ?? 0) + installationSpanMm(module)));
  const fits = (width: number) => {
    const trial = withRoomWidth(project, width);
    const trialWalls = wallsOf(trial);
    return trial.modules.filter(isFloorModule).every(module =>
      trialWalls.get(module.id) === walls.get(module.id) &&
      (overlapping.has(module.id) || !moduleOverlapsFloor(trial, module.id)));
  };
  let low = Math.min(current, Math.max(MIN_ROOM_WIDTH_MM, Math.ceil(backRight + reserve)));
  if (low >= current || fits(low)) return low;
  // Текущая ширина подходит всегда: ищем границу делением пополам с точностью 5 мм.
  let high = current;
  while (high - low > 5) {
    const middle = Math.round((low + high) / 2);
    if (fits(middle)) high = middle; else low = middle;
  }
  return high;
});

/**
 * Тот же проект в комнате другой ширины: шкафы у задней стены стоят на месте
 * (отсчёт от левого угла), шкафы у правой стены едут вместе со стеной.
 * Места всех изделий сначала закрепляются, чтобы автоматически расставленные
 * не перескочили к другой стене.
 */
export const withRoomWidth = (project: PlannerProject, width: number): PlannerProject => {
  const previous = roomWidthMm(project);
  const offsets = withRoomOf(project, () => materializeWallOffsets(project));
  const room = { ...project.room, width: roomWidthMm({ room: { ...project.room, width } }) };
  const delta = room.width - previous;
  return {
    ...project,
    room,
    modules: project.modules.map(module => {
      if (module.stackedOnId || module.type === "corner45") return module;
      const offset = offsets.get(module.id) ?? module.wallOffset ?? 0;
      return { ...module, wallOffset: offset >= previous ? offset + delta : offset };
    })
  };
};

/**
 * Куда встанет шкаф, если его тащат вместе с торцами: место ищется для всей
 * группы. Одна функция для сцены (предпросмотр) и для магазина (итог).
 */
/** Сколько занимают торцы слева и справа от шкафа, если стоят вплотную (зазор 1 мм). */
const attachedEndSpans = (ends: WardrobeModule[]) => ({
  leading: ends.filter(end => end.endSide === "left").reduce((sum, end) => sum + installationSpanMm(end) + WALL_GAP_MM, 0),
  trailing: ends.filter(end => end.endSide !== "left").reduce((sum, end) => sum + installationSpanMm(end) + WALL_GAP_MM, 0)
});

export const resolveModuleGroupWallOffset = (
  project: PlannerProject,
  moduleId: string,
  desiredOffset: number,
  direction: number
) => {
  const module = project.modules.find(candidate => candidate.id === moduleId);
  if (!module) return desiredOffset;
  const ends = module.type === "single" || module.type === "double"
    ? project.modules.filter(candidate => candidate.type === "end" && candidate.endAttachedToId === module.id)
    : [];
  if (!ends.length) return resolveModuleWallOffset(project, moduleId, desiredOffset, direction);
  const offsets = materializeWallOffsets(project);
  const members = [module, ...ends];
  const { leading: lead, trailing } = attachedEndSpans(ends);
  const start = (offsets.get(module.id) ?? 0) - lead;
  const proxy: WardrobeModule = {
    ...module, width: installationSpanMm(module) + lead + trailing, depth: Math.max(...members.map(member => member.depth)),
    fillerLeft: 0, fillerRight: 0, wallOffset: start
  };
  const trial: PlannerProject = {
    ...project,
    modules: [
      ...project.modules.filter(candidate => !members.includes(candidate))
        .map(candidate => ({ ...candidate, wallOffset: offsets.get(candidate.id) ?? candidate.wallOffset })),
      proxy
    ]
  };
  return resolveModuleWallOffset(trial, module.id, desiredOffset - lead, direction) + lead;
};

/** Шкаф вместе с торцами, поставленный на место at: как одна деталь для проверки. */
const moduleGroupProxy = (project: PlannerProject, module: WardrobeModule, at: number) => {
  const ends = module.type === "single" || module.type === "double"
    ? project.modules.filter(candidate => candidate.type === "end" && candidate.endAttachedToId === module.id)
    : [];
  const { leading, trailing } = attachedEndSpans(ends);
  const members = [module, ...ends];
  const proxy: WardrobeModule = ends.length ? {
    ...module, width: installationSpanMm(module) + leading + trailing, depth: Math.max(...members.map(member => member.depth)),
    fillerLeft: 0, fillerRight: 0, wallOffset: at - leading
  } : { ...module, wallOffset: at };
  return { proxy, members };
};

/** Встаёт ли шкаф с торцами на это место: целиком на своей стене и ни на кого не налезает. */
export const moduleGroupFitsAt = (project: PlannerProject, moduleId: string, at: number) => {
  const module = project.modules.find(candidate => candidate.id === moduleId);
  if (!module || module.stackedOnId) return false;
  const { proxy, members } = moduleGroupProxy(project, module, at);
  if (!fitsOnWall(proxy, proxy.wallOffset!)) return false;
  const offsets = materializeWallOffsets(project);
  const trial: PlannerProject = {
    ...project,
    modules: [
      ...project.modules.filter(candidate => !members.includes(candidate))
        .map(candidate => ({ ...candidate, wallOffset: offsets.get(candidate.id) ?? candidate.wallOffset })),
      proxy
    ]
  };
  return !moduleOverlapsFloor(trial, proxy.id);
};

/** Ближайшее к желаемому свободное место для шкафа с торцами; null — места нет. */
export const placeModuleGroup = (project: PlannerProject, moduleId: string, desiredOffset: number, direction: number) => {
  const at = resolveModuleGroupWallOffset(project, moduleId, desiredOffset, direction);
  return moduleGroupFitsAt(project, moduleId, at) ? at : null;
};
